import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

const API =
  process.env.REACT_APP_API_URL ||
  "https://api.wirelesspress.org";

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatDate(value) {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatLabel(value) {
  if (!value) {
    return "Unknown";
  }

  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function ThreatIntelligencePage() {
  const [dashboard, setDashboard] =
    useState(null);

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [error, setError] =
    useState("");

  const loadDashboard = useCallback(
    async (silent = false) => {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const response = await apiFetch(`/threat-intel/dashboard`
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data.detail ||
            "Unable to load threat intelligence dashboard."
          );
        }

        setDashboard(data);
      } catch (loadError) {
        console.error(
          "Threat intelligence dashboard error:",
          loadError
        );

        setError(
          loadError.message ||
          "Unable to load threat intelligence dashboard."
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadDashboard();

    const refreshTimer = window.setInterval(
      () => loadDashboard(true),
      30000
    );

    return () =>
      window.clearInterval(
        refreshTimer
      );
  }, [loadDashboard]);

  const summary =
    dashboard?.summary || {};

  const indicatorTypes =
    dashboard?.indicator_types || [];

  const severityDistribution =
    dashboard?.severity_distribution || [];

  const threatCategories =
    dashboard?.threat_categories || [];

  const recentIndicators =
    dashboard?.recent_high_risk_indicators ||
    [];

  const feeds =
    dashboard?.feeds || [];

  const maxTypeCount = useMemo(
    () =>
      Math.max(
        ...indicatorTypes.map(
          (item) => Number(item.count || 0)
        ),
        1
      ),
    [indicatorTypes]
  );

  const maxSeverityCount = useMemo(
    () =>
      Math.max(
        ...severityDistribution.map(
          (item) => Number(item.count || 0)
        ),
        1
      ),
    [severityDistribution]
  );

  const maxCategoryCount = useMemo(
    () =>
      Math.max(
        ...threatCategories.map(
          (item) => Number(item.count || 0)
        ),
        1
      ),
    [threatCategories]
  );

  const metrics = [
    {
      label: "Total Indicators",
      value: summary.total_indicators,
      detail: `${formatNumber(
        summary.active_indicators
      )} active`,
    },
    {
      label: "Critical IOCs",
      value: summary.critical_indicators,
      detail: "Immediate attention",
    },
    {
      label: "High IOCs",
      value: summary.high_indicators,
      detail: "Elevated risk",
    },
    {
      label: "New in 24 Hours",
      value: summary.new_last_24_hours,
      detail: "Recently ingested",
    },
    {
      label: "IOC Matches",
      value: summary.matches_last_24_hours,
      detail: "Past 24 hours",
    },
    {
      label: "Active Feeds",
      value: summary.enabled_feeds,
      detail: `${formatNumber(
        summary.total_feeds
      )} configured`,
    },
    {
      label: "Feed Health",
      value: `${
        summary.feed_health_percentage || 0
      }%`,
      detail: `${formatNumber(
        summary.feeds_with_errors
      )} feed errors`,
    },
    {
      label: "Expired IOCs",
      value: summary.expired_indicators,
      detail: "Review or retire",
    },
  ];

  return (
    <div className="threatIntelPage">
      <header className="threatIntelHeader">
        <div>
          <p className="threatIntelEyebrow">
            WirelessPress Threat Intelligence
          </p>

          <h1>
            Threat Intelligence Center
          </h1>

          <p>
            Monitor indicators, intelligence
            feeds, IOC matches, threat actors,
            campaigns, and emerging threats.
          </p>
        </div>

        <button
          type="button"
          className="threatIntelRefreshButton"
          disabled={refreshing}
          onClick={() =>
            loadDashboard(true)
          }
        >
          {refreshing
            ? "Refreshing..."
            : "Refresh Intelligence"}
        </button>
      </header>

      {error ? (
        <div className="threatIntelError">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="threatIntelLoading">
          Loading threat intelligence...
        </div>
      ) : null}

      {!loading && dashboard ? (
        <>
          <section className="threatIntelMetrics">
            {metrics.map((metric) => (
              <article key={metric.label}>
                <span>{metric.label}</span>

                <strong>
                  {typeof metric.value ===
                  "string"
                    ? metric.value
                    : formatNumber(
                        metric.value
                      )}
                </strong>

                <small>
                  {metric.detail}
                </small>
              </article>
            ))}
          </section>

          <section className="threatIntelGrid">
            <article className="threatIntelPanel">
              <div className="threatIntelPanelHeader">
                <div>
                  <span>
                    IOC DISTRIBUTION
                  </span>

                  <h2>
                    Indicator Types
                  </h2>
                </div>
              </div>

              <div className="threatIntelBars">
                {indicatorTypes.length ===
                0 ? (
                  <p className="threatIntelEmpty">
                    No indicators available.
                  </p>
                ) : null}

                {indicatorTypes.map(
                  (item) => (
                    <div
                      className="threatIntelBarRow"
                      key={
                        item.indicator_type
                      }
                    >
                      <div>
                        <span>
                          {formatLabel(
                            item.indicator_type
                          )}
                        </span>

                        <strong>
                          {formatNumber(
                            item.count
                          )}
                        </strong>
                      </div>

                      <div className="threatIntelBarTrack">
                        <div
                          className="threatIntelBarFill"
                          style={{
                            width: `${
                              (Number(
                                item.count ||
                                  0
                              ) /
                                maxTypeCount) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            </article>

            <article className="threatIntelPanel">
              <div className="threatIntelPanelHeader">
                <div>
                  <span>
                    RISK PROFILE
                  </span>

                  <h2>
                    Severity Distribution
                  </h2>
                </div>
              </div>

              <div className="threatIntelBars">
                {severityDistribution.length ===
                0 ? (
                  <p className="threatIntelEmpty">
                    No severity data available.
                  </p>
                ) : null}

                {severityDistribution.map(
                  (item) => (
                    <div
                      className="threatIntelBarRow"
                      key={item.severity}
                    >
                      <div>
                        <span>
                          {formatLabel(
                            item.severity
                          )}
                        </span>

                        <strong>
                          {formatNumber(
                            item.count
                          )}
                        </strong>
                      </div>

                      <div className="threatIntelBarTrack">
                        <div
                          className={`threatIntelBarFill severity-${String(
                            item.severity
                          ).toLowerCase()}`}
                          style={{
                            width: `${
                              (Number(
                                item.count ||
                                  0
                              ) /
                                maxSeverityCount) *
                              100
                            }%`,
                          }}
                        />
                      </div>
                    </div>
                  )
                )}
              </div>
            </article>
          </section>

          <section className="threatIntelPanel">
            <div className="threatIntelPanelHeader">
              <div>
                <span>
                  INTELLIGENCE CLASSIFICATION
                </span>

                <h2>
                  Top Threat Categories
                </h2>
              </div>
            </div>

            <div className="threatIntelCategoryGrid">
              {threatCategories.length ===
              0 ? (
                <p className="threatIntelEmpty">
                  No threat categories available.
                </p>
              ) : null}

              {threatCategories.map(
                (item) => (
                  <article
                    key={
                      item.threat_category
                    }
                  >
                    <div>
                      <strong>
                        {
                          item.threat_category
                        }
                      </strong>

                      <span>
                        {formatNumber(
                          item.count
                        )}{" "}
                        indicators
                      </span>
                    </div>

                    <div className="threatIntelCategoryTrack">
                      <div
                        style={{
                          width: `${
                            (Number(
                              item.count || 0
                            ) /
                              maxCategoryCount) *
                            100
                          }%`,
                        }}
                      />
                    </div>
                  </article>
                )
              )}
            </div>
          </section>

          <section className="threatIntelPanel">
            <div className="threatIntelPanelHeader">
              <div>
                <span>
                  PRIORITY INVESTIGATION
                </span>

                <h2>
                  Recent High-Risk Indicators
                </h2>
              </div>
            </div>

            <div className="threatIntelTableWrapper">
              <table className="threatIntelTable">
                <thead>
                  <tr>
                    <th>Indicator</th>
                    <th>Type</th>
                    <th>Severity</th>
                    <th>Confidence</th>
                    <th>Reputation</th>
                    <th>Feed</th>
                    <th>Threat Context</th>
                    <th>Created</th>
                  </tr>
                </thead>

                <tbody>
                  {recentIndicators.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan="8"
                        className="threatIntelEmptyCell"
                      >
                        No critical or
                        high-risk indicators.
                      </td>
                    </tr>
                  ) : null}

                  {recentIndicators.map(
                    (indicator) => (
                      <tr
                        key={indicator.id}
                      >
                        <td>
                          <strong className="threatIntelIndicatorValue">
                            {
                              indicator.indicator_value
                            }
                          </strong>

                          <small>
                            {indicator.title ||
                              "Untitled indicator"}
                          </small>
                        </td>

                        <td>
                          {formatLabel(
                            indicator.indicator_type
                          )}
                        </td>

                        <td>
                          <span
                            className={`threatIntelSeverity severity-${String(
                              indicator.severity
                            ).toLowerCase()}`}
                          >
                            {formatLabel(
                              indicator.severity
                            )}
                          </span>
                        </td>

                        <td>
                          {
                            indicator.confidence
                          }
                          %
                        </td>

                        <td>
                          {indicator.reputation_score ??
                            "—"}
                        </td>

                        <td>
                          {indicator.feed_name ||
                            "Internal"}
                        </td>

                        <td>
                          {indicator.malware_family ||
                            indicator.threat_actor ||
                            indicator.campaign ||
                            indicator.threat_category ||
                            "Unclassified"}
                        </td>

                        <td>
                          {formatDate(
                            indicator.created_at
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <section className="threatIntelPanel">
            <div className="threatIntelPanelHeader">
              <div>
                <span>
                  COLLECTION HEALTH
                </span>

                <h2>
                  Threat Feed Status
                </h2>
              </div>
            </div>

            <div className="threatIntelFeedGrid">
              {feeds.length === 0 ? (
                <p className="threatIntelEmpty">
                  No intelligence feeds
                  configured.
                </p>
              ) : null}

              {feeds.map((feed) => (
                <article key={feed.id}>
                  <div className="threatIntelFeedHeader">
                    <div>
                      <strong>
                        {feed.name}
                      </strong>

                      <span>
                        {formatLabel(
                          feed.feed_type
                        )}
                      </span>
                    </div>

                    <span
                      className={`threatIntelFeedStatus status-${feed.health_status}`}
                    >
                      {formatLabel(
                        feed.health_status
                      )}
                    </span>
                  </div>

                  <dl>
                    <div>
                      <dt>
                        Total IOCs
                      </dt>
                      <dd>
                        {formatNumber(
                          feed.indicator_count
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Active IOCs
                      </dt>
                      <dd>
                        {formatNumber(
                          feed.active_indicator_count
                        )}
                      </dd>
                    </div>

                    <div>
                      <dt>
                        Last Success
                      </dt>
                      <dd>
                        {formatDate(
                          feed.last_success_at
                        )}
                      </dd>
                    </div>
                  </dl>

                  {feed.last_error ? (
                    <p className="threatIntelFeedError">
                      {feed.last_error}
                    </p>
                  ) : null}
                </article>
              ))}
            </div>
          </section>

          <footer className="threatIntelFooter">
            Last updated:{" "}
            {formatDate(
              dashboard.generated_at
            )}
          </footer>
        </>
      ) : null}
    </div>
  );
}

export default ThreatIntelligencePage;
